import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

interface WizardProgressProps {
  currentStep: number;
  totalSteps: number;
  labels?: string[];
  /** Optional sub-step label shown below the step counter, e.g. "School Type (1 of 5)" */
  subStepInfo?: string;
}

export function WizardProgress({ currentStep, totalSteps, labels, subStepInfo }: WizardProgressProps) {
  const percentage = (currentStep / totalSteps) * 100;

  return (
    <div className="w-full mb-8">
      {/* Step counter */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-sm font-semibold text-primary">
            Step {currentStep} of {totalSteps}
          </span>
          {subStepInfo && (
            <span className="ml-2 text-xs font-medium text-muted-foreground">
              · {subStepInfo}
            </span>
          )}
        </div>
        <span className="text-sm font-medium text-muted-foreground">
          {Math.round(percentage)}% complete
        </span>
      </div>

      {/* Progress bar */}
      <div className="progress-bar">
        <motion.div
          className="progress-bar-fill"
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>

      {/* Step indicators (optional) */}
      {labels && (
        <div className="flex justify-between mt-4">
          {labels.map((label, index) => {
            const stepNum = index + 1;
            const isComplete = stepNum < currentStep;
            const isCurrent = stepNum === currentStep;

            return (
              <div key={label} className="flex flex-col items-center">
                <motion.div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                    transition-colors duration-300
                    ${isComplete ? 'bg-success text-success-foreground' : ''}
                    ${isCurrent ? 'bg-primary text-primary-foreground' : ''}
                    ${!isComplete && !isCurrent ? 'bg-muted text-muted-foreground' : ''}
                  `}
                  initial={false}
                  animate={{ scale: isCurrent ? 1.1 : 1 }}
                >
                  {isComplete ? <Check className="w-4 h-4" /> : stepNum}
                </motion.div>
                <span className={`
                  mt-2 text-xs font-medium hidden md:block
                  ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}
                `}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
